import React from 'react'

import SubmitESignatureButton from '@/components/misc/ReferralESignature/components/SubmitESignatureButton'
import ESignatureUnderstandingCheckbox from '@/components/misc/ReferralESignatureConsentPdf/components/ESignatureUnderstandingCheckbox'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import { REFERRAL_CONSENT_STAGE, YELLOW_BUTTON_PROPS } from '@/lib/constants'
import {
  selectHasConfirmedESignatureUnderstanding,
  selectHasConfirmedRetrospectiveConsentUnderstanding,
  selectIsShowingSubmitModal,
  setIsShowingSubmitModal,
} from '@/redux/slices/referralSignatureConsentPdf'
import { ReferralConsentStage } from '@/types/misc'

interface Props {
  onSubmit?: () => void
  isSubmitting?: boolean
  ctaText?: string
  consentStage: ReferralConsentStage
}

const ConfirmESignatureSubmissionModal: React.FC<Props> = ({
  onSubmit,
  isSubmitting = false,
  ctaText = 'Submit',
  consentStage,
}) => {
  const isShowingSubmitModal = useAppSelector(selectIsShowingSubmitModal)
  const hasConfirmedRetrospectiveConsentUnderstanding = useAppSelector(
    selectHasConfirmedRetrospectiveConsentUnderstanding,
  )
  const hasConfirmedESignatureUnderstanding = useAppSelector(
    selectHasConfirmedESignatureUnderstanding,
  )

  const isRetrospectiveConsent =
    consentStage === REFERRAL_CONSENT_STAGE.RETROSPECTIVE

  const dispatch = useAppDispatch()

  function onHide() {
    dispatch(setIsShowingSubmitModal(false))
  }

  if (!isShowingSubmitModal || !onSubmit) {
    return null
  }

  const shouldDisableSubmitButton = isRetrospectiveConsent
    ? !hasConfirmedRetrospectiveConsentUnderstanding ||
      !hasConfirmedESignatureUnderstanding ||
      isSubmitting
    : !hasConfirmedESignatureUnderstanding || isSubmitting

  return (
    <Dialog open onHide={onHide}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Almost there</DialogTitle>
        </DialogHeader>

        <DialogBody>
          <Alert variant="info" className="mb-4">
            <AlertDescription className="flex items-start gap-2">
              <ESignatureUnderstandingCheckbox
                isSubmittingSignature={isSubmitting}
                consentStage={consentStage}
              />
            </AlertDescription>
          </Alert>

          <p className="mb-0">
            Almost there: Press <em>{ctaText}</em> to proceed.
          </p>
        </DialogBody>

        <DialogFooter>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="w-full sm:w-auto">
                <SubmitESignatureButton
                  {...YELLOW_BUTTON_PROPS}
                  onSubmitConsent={onSubmit}
                  isProcessing={isSubmitting}
                  isProcessingText="Submitting"
                  disabled={shouldDisableSubmitButton}
                >
                  {ctaText}
                </SubmitESignatureButton>
              </div>
            </TooltipTrigger>

            {!hasConfirmedESignatureUnderstanding && (
              <TooltipContent>
                Please tick the checkbox above to confirm you understand that a
                electronic signature is as valid and legally binding as a
                handwritten signature.
              </TooltipContent>
            )}
          </Tooltip>

          <Button
            variant="outline-secondary"
            className="w-full sm:order-first sm:w-auto"
            onClick={onHide}
            size="lg"
            disabled={isSubmitting}
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default ConfirmESignatureSubmissionModal
